import type { IReservation } from '@curbnturf/entities';
import type { IBooking } from '@cnt-workspace/models';
import { HelperFunctions, PriceDisplay, Status } from '@curbnturf/entities';
import { rvTypeLabel } from '../my-rv.util';
import { roundMoney, toBookingView } from './reservation-real-mapper';

/** One line of the host's money breakdown. */
export interface IHostMoneyLine {
  label: string;
  amount: number;
}

/**
 * The host's money for one version of a booking, laid out exactly like the
 * server's host booking emails (router.service.ts "grossIncome / expenses /
 * netIncome / taxes / takeHomePay"), so the page and the email agree.
 */
export interface IHostMoney {
  /** Nights and add-ons, at the price the guest pays before tax. */
  lines: IHostMoneyLine[];
  /** Shown as negatives. */
  discounts: IHostMoneyLine[];
  gross: number;
  curbnturfFee: number;
  feedbackIncentive: number;
  /** What the host earns: gross minus the fee and the incentive. */
  earnings: number;
  taxes: IHostMoneyLine[];
  /** Earnings plus the taxes the host collects and remits. */
  takeHome: number;
}

/** The guest's rig as a host may see it: what's needed to judge fit, never the plate. */
export interface IHostRig {
  type: string;
  title: string;
  length?: number;
  width?: number;
  height?: number;
  towVehicle?: string;
  tents?: number;
  photoUrl?: string;
}

/** When the host must answer a request by. `estimated` until the server sends its request window. */
export interface IHostDeadline {
  at?: Date;
  estimated: boolean;
}

export interface IHostReservationView {
  id: number;
  /** The `IBooking` id (`res-N`) that BookingService actions take. */
  bookingId: string;
  status: ReturnType<typeof toBookingView>['status'];
  changeRequested: boolean;
  /** Waiting on the host: a new request or a guest's change. */
  awaitingHost: boolean;
  listingId: number;
  listingName: string;
  /** The listing's first photo, for the page's header. */
  listingPhoto?: string;
  siteName: string;
  checkIn: string;
  checkOut: string;
  checkInTime?: string;
  checkOutTime?: string;
  nights: number;
  guests?: number;
  confirmation: string;
  guest: { id?: number; firstName: string; lastInitial: string; photoUrl?: string; memberSince?: Date };
  rig?: IHostRig;
  addOns: { label: string; count: number }[];
  money: IHostMoney;
  /** The guest's requested version, while a change waits on the host. */
  requested?: { checkIn: string; checkOut: string; nights: number; guests?: number; money: IHostMoney };
  deadline?: IHostDeadline;
  cancellationPolicy?: string;
  cancelReason?: string;
}

/** How long the host has to answer a request (server/src/shared/constants.ts). */
export const HOST_RESPONSE_HOURS = 24;

export function hostMoneyFrom(prices: PriceDisplay | null): IHostMoney {
  if (!prices) {
    return {
      lines: [],
      discounts: [],
      gross: 0,
      curbnturfFee: 0,
      feedbackIncentive: 0,
      earnings: 0,
      taxes: [],
      takeHome: 0,
    };
  }
  const line = (l: { key: string; value: number }) => ({ label: l.key, amount: roundMoney(l.value) });
  return {
    lines: [...(prices.nights ?? []), ...(prices.extras ?? [])].map(line),
    discounts: (prices.discounts ?? []).filter((d) => d.value !== 0).map(line),
    gross: roundMoney(prices.hostGross ?? 0),
    curbnturfFee: roundMoney(prices.fees ?? 0),
    feedbackIncentive: roundMoney(prices.feedbackIncentive ?? 0),
    earnings: roundMoney(prices.hostNet ?? 0),
    taxes: (prices.taxes ?? []).filter((t) => t.value !== 0).map(line),
    takeHome: roundMoney((prices.hostNet ?? 0) + (prices.tax ?? 0)),
  };
}

function rigFrom(reservation: IReservation): IHostRig | undefined {
  const rv = reservation.rv;
  if (!rv) return undefined;
  const type = rvTypeLabel(rv.type);
  const title = [rv.year, rv.make, rv.model].filter(Boolean).join(' ') || rv.name || type;
  const tow = rv.towVehicle;
  const towVehicle = tow ? [tow.year, tow.make, tow.model].filter(Boolean).join(' ') || undefined : undefined;
  // Plate number, plate state and the plate photo stay out on purpose (Dustin, 2026-10-27).
  return {
    type,
    title,
    length: rv.length || undefined,
    width: rv.width || undefined,
    height: rv.height || undefined,
    towVehicle,
    tents: rv.tent ? rv.tentCount || 1 : undefined,
    photoUrl: rv.photo?.key ? HelperFunctions.getPhotoUrlFullSize(rv.photo) : undefined,
  };
}

/**
 * The open request window's deadline when the server sends one; until then, an
 * estimate: a new request is due 24h after it was made. A change request's
 * start time isn't on the reservation, so it gets no time.
 */
export function hostDeadlineFrom(reservation: IReservation, changeRequested: boolean): IHostDeadline | undefined {
  const open = reservation.requestWindows?.filter((w) => !w.resolvedAt).sort((a, b) => b.requestedAt - a.requestedAt)[0];
  if (open) {
    const seconds =
      open.graceDeadline && open.graceDeadline > open.responseDeadline ? open.graceDeadline : open.responseDeadline;
    return { at: new Date(seconds * 1000), estimated: false };
  }
  if (changeRequested) return { estimated: true };
  const requested = reservation.activity?.find((a) => a.status === Status.requested)?.timestamp;
  return requested
    ? { at: new Date((requested + HOST_RESPONSE_HOURS * 3600) * 1000), estimated: true }
    : { estimated: true };
}

export function toHostReservationView(reservation: IReservation): IHostReservationView {
  const booking = toBookingView(reservation);
  const listing = reservation.site?.listing;
  const user = reservation.user;
  const changeRequested = !!booking.changeRequested;
  const awaitingHost = booking.status === 'pending' && reservation.status !== Status.processing;
  const prices =
    (reservation.priceComponents ?? []).length > 0 ? new PriceDisplay(reservation.priceComponents ?? [], true) : null;
  const pending = booking.pendingChange;
  const pendingRows = (reservation.priceComponents ?? []).filter((p) => p.pendingChange === true);
  return {
    id: reservation.id as number,
    bookingId: booking.id,
    status: booking.status,
    changeRequested,
    awaitingHost,
    listingId: booking.listingId,
    listingName: listing?.name || booking.listingTitle,
    listingPhoto: booking.listingPhoto || undefined,
    siteName: reservation.site?.name || '',
    checkIn: booking.dates.start,
    checkOut: booking.dates.end,
    checkInTime: (listing?.checkIn as string | undefined) || undefined,
    checkOutTime: (listing?.checkOut as string | undefined) || undefined,
    nights: booking.nights,
    guests: booking.guests,
    confirmation: String(reservation.id ?? ''),
    guest: {
      id: user?.id,
      firstName: user?.firstName?.trim() || 'Guest',
      lastInitial: (user?.lastName?.trim().charAt(0) ?? '').toUpperCase(),
      photoUrl: user?.photo?.key ? HelperFunctions.getPhotoUrlSquareSmall(user.photo) : undefined,
      memberSince: user?.created ? new Date(Number(user.created) * 1000) : undefined,
    },
    rig: rigFrom(reservation),
    addOns: (reservation.extras ?? [])
      .filter((e) => e.pendingChange !== true)
      .map((e) => ({ label: e.extra?.name || e.extra?.label || 'Add-on', count: e.count || 1 })),
    money: hostMoneyFrom(prices),
    requested:
      changeRequested && pending
        ? {
            checkIn: pending.start,
            checkOut: pending.end,
            nights: pending.nights,
            guests: pending.guests,
            money: hostMoneyFrom(pendingRows.length > 0 ? new PriceDisplay(pendingRows) : null),
          }
        : undefined,
    deadline: awaitingHost ? hostDeadlineFrom(reservation, changeRequested) : undefined,
    cancellationPolicy: booking.cancellationPolicy,
    cancelReason: booking.cancelReason,
  };
}

/**
 * The same estimate for a booking from the host's list, which carries no
 * request window: a new request is due 24h after it was made (`createdAt`).
 */
export function bookingDeadline(b: IBooking): IHostDeadline | undefined {
  if (b.status !== 'pending') return undefined;
  if (b.changeRequested) return { estimated: true };
  const made = new Date(b.createdAt).getTime();
  return Number.isFinite(made)
    ? { at: new Date(made + HOST_RESPONSE_HOURS * 3600_000), estimated: true }
    : { estimated: true };
}
